/**
 * Home dashboard: builds the section list from sidebar RBAC and saved layout prefs.
 */

import * as React from 'react';
import { DashboardLayout } from './DashboardLayout';
import { ErrorBoundary } from '@/components/common/ErrorBoundary';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { Layout } from 'lucide-react';
import { usePreferencesConfig } from '@/hooks/usePreferencesConfig';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useAuthStore } from '@/stores/authStore';
import { useMode } from '@/contexts/ModeContext';
import type { DashboardSection, SavedDashboardLayout } from '@/types/dashboard.types';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  getDashboardPreferenceKey,
  initializeDashboardSections,
  sortSectionsForEditor,
  toggleSectionVisibility,
  toSavedDashboardLayout,
  visibleDashboardSections,
} from './dashboard-utils';

/** Home dashboard: builds the section list from sidebar RBAC and saved layout prefs. */
export const DashboardPage: React.FC = () => {
  const { mode } = useMode();
  const dashboardConfigKey = getDashboardPreferenceKey(mode);
  const { config: dashboardConfig, saveConfig } = usePreferencesConfig(dashboardConfigKey);
  const { elements: sidebarMenus, isLoading: sidebarLoading } = useInterfaceElements('sidebar');
  const [sections, setSections] = React.useState<DashboardSection[]>([]);
  const [showLayoutModal, setShowLayoutModal] = React.useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const initialSectionsRef = React.useRef<DashboardSection[]>([]);

  /** Reset initialization when user/roles or mode changes (forces re-filter). */
  const userRoles = useAuthStore((state) => state.roles);
  React.useEffect(() => {
    setIsInitialized(false);
  }, [userRoles, mode]);

  React.useEffect(() => {
    if (isInitialized || sidebarLoading) return;
    setSections(
      initializeDashboardSections(
        sidebarMenus,
        dashboardConfig as SavedDashboardLayout | undefined,
      ),
    );
    setIsInitialized(true);
  }, [isInitialized, sidebarLoading, dashboardConfig, sidebarMenus]);

  /** Snapshot current sections so Cancel can restore them. */
  const handleOpenModal = () => {
    initialSectionsRef.current = [...sections];
    setShowLayoutModal(true);
    setHasUnsavedChanges(false);
  };

  /** Restore the snapshot taken when the layout modal opened. */
  const handleCancelModal = () => {
    if (initialSectionsRef.current.length > 0) {
      setSections(initialSectionsRef.current);
    }
    setShowLayoutModal(false);
    setHasUnsavedChanges(false);
  };

  /** Toggle a widget in the editor; marks the modal dirty. */
  const handleToggleVisibility = (sectionId: string) => {
    setSections((prev) => toggleSectionVisibility(prev, sectionId));
    setHasUnsavedChanges(true);
  };

  /** Persist order / width / visibility to user preferences. */
  const handleSaveModal = () => {
    saveConfig(toSavedDashboardLayout(sections));
    setShowLayoutModal(false);
    setHasUnsavedChanges(false);
  };

  const sortedSectionsForModal = React.useMemo(() => sortSectionsForEditor(sections), [sections]);

  return (
    <ErrorBoundary
      fallback={
        <div className="p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">{getErrorMessage('loadDashboardFailed')}</h2>
          <p className="text-muted-foreground">{getErrorMessage('loadDashboardFailedDetail')}</p>
        </div>
      }
    >
      <div className="p-6 space-y-8">
        <div className="flex justify-end">
          <Button icon={Layout} onClick={handleOpenModal} variant="outline" size="lg">
            Ajuster mes widgets
          </Button>
        </div>

        <DashboardLayout
          sections={visibleDashboardSections(sections)}
          onSectionsChange={setSections}
          editMode={false}
          previewMode={false}
          scale={1}
        />

        <BaseModal
          open={showLayoutModal}
          onClose={handleCancelModal}
          size="xl"
          className="dashboard-editor-modal"
          hideContentWrapper={true}
          header={{
            title: 'Éditer la disposition du dashboard',
            titleSize: 'lg',
          }}
          footer={
            <div className="flex gap-2 justify-end w-full">
              <Button preset="modal-cancel" onClick={handleCancelModal}>
                Annuler
              </Button>
              <Button
                preset="modal-confirm"
                onClick={handleSaveModal}
                disabled={!hasUnsavedChanges}
              >
                Sauvegarder
              </Button>
            </div>
          }
        >
          <div className="dashboard-editor-container">
            <DashboardLayout
              sections={sortedSectionsForModal}
              onSectionsChange={setSections}
              editMode={true}
              previewMode={true}
              scale={1}
              onUnsavedChanges={setHasUnsavedChanges}
              onToggleVisibility={handleToggleVisibility}
            />
          </div>
        </BaseModal>
      </div>
    </ErrorBoundary>
  );
};
